import * as React from "react";

import {isNonNullable} from "@khanacademy/wonder-stuff-core";
import {Persist, useFastlyQuery} from "@khan/data-gql";

import {LOCALES_QUERY, type Locale as LocaleType} from "./locales-query.ts";

type NonNullableFields<T> = {
    [P in keyof T]: T[P] extends infer U | null | undefined ? U : T[P];
};

// TODO(michaelpolyak): Whenever the `Locale` object type fields in the schema
// are made required, switch to using `kaLocale` instead of `id`, since it's
// more explicitly obvious.
export type Locale = NonNullableFields<
    Omit<LocaleType, "__typename" | "kaLocale">
>;

interface LocalesQueryResult {
    locales: Array<Locale>;
    isLoading: boolean;
    isError: boolean;
}
export const useLocalesQuery = (): LocalesQueryResult => {
    const result = useFastlyQuery(LOCALES_QUERY, {
        cachingPolicy: Persist.AcrossPublish,
    });

    return React.useMemo(() => {
        const localesForLanguagePicker =
            result.status === "success"
                ? result.data.localesForLanguagePicker
                : null;

        if (result.status === "loading" || !localesForLanguagePicker) {
            return {
                isError: result.status === "error",
                isLoading: result.status === "loading",
                locales: [],
            };
        }

        const locales = [...localesForLanguagePicker]
            .reduce((acc: Locale[], locale) => {
                if (
                    isNonNullable(locale) &&
                    isNonNullable(locale.localName) &&
                    isNonNullable(locale.id)
                ) {
                    acc.push({
                        id: locale.id,
                        localName: locale.localName,
                        isSupportedInKhanClassroom:
                            locale.isSupportedInKhanClassroom,
                        khanmigoSupport: locale.khanmigoSupport,
                    });
                }
                return acc;
            }, [])
            .sort((a, b): number => a.localName.localeCompare(b.localName));

        return {
            isError: false,
            isLoading: false,
            locales,
        };
    }, [result]);
};

export default useLocalesQuery;
